import type { SupabaseClient } from '@supabase/supabase-js';

export async function addNoteEntry(opts: {
  supabaseClient?: SupabaseClient | any;
  groupId: string;
  dayNumber: number;
  dayDate?: string | null;
  afterEntryId?: string | null;
  creatorId?: string | null;
  noteText: string;
  start_time?: string | null;
  end_time?: string | null;
  parentEntryId?: string | null;
  childSort?: number | null;
  mapLink?: string | null;
  mapLinkMode?: string | null;
  mapLinkDuration?: string | null;
}) {
  const {
    supabaseClient,
    groupId,
    dayNumber,
    dayDate = null,
    afterEntryId = null,
    creatorId = null,
    noteText,
    start_time = null,
    end_time = null,
    parentEntryId = null,
    childSort = null,
    mapLink = null,
    mapLinkMode = null,
    mapLinkDuration = null,
  } = opts;

  const client = supabaseClient ?? (await import('@cubemeet/core/services/supabase')).supabase;

  // Compute sort_order
  let newSort = 0;
  const isChildNote = Boolean(parentEntryId);

  try {
    if (isChildNote && parentEntryId) {
      try {
        const parentRes: any = await client
          .from('group_itinerary_entries')
          .select('sort_order')
          .eq('id', parentEntryId)
          .maybeSingle();
        const parent = parentRes && (parentRes.data ?? parentRes);
        newSort = parent && typeof parent.sort_order === 'number' ? parent.sort_order : 0;
      } catch (e) {
        newSort = 0;
      }
    } else if (afterEntryId) {
      // find previous entry
      const prevRes: any = await client.from('group_itinerary_entries').select('sort_order').eq('id', afterEntryId).maybeSingle();
      const prev = prevRes && (prevRes.data ?? prevRes);
      const prevSort = prev && typeof prev.sort_order === 'number' ? prev.sort_order : null;

      if (prevSort === null) {
        // fallback to append
        const maxRes: any = await client.from('group_itinerary_entries').select('sort_order').eq('group_id', groupId).eq('day_number', dayNumber).order('sort_order', { ascending: false }).limit(1).maybeSingle();
        const maxRow = maxRes && (maxRes.data ?? maxRes);
        newSort = (maxRow && typeof maxRow.sort_order === 'number' ? maxRow.sort_order : 0) + 1;
      } else {
        // attempt to find next entry to create a gap
        const nextRes: any = await client.from('group_itinerary_entries').select('sort_order').eq('group_id', groupId).eq('day_number', dayNumber).gt('sort_order', prevSort).order('sort_order', { ascending: true }).limit(1).maybeSingle();
        const next = nextRes && (nextRes.data ?? nextRes);
        if (next && typeof next.sort_order === 'number') {
          const nextSort = next.sort_order;
          if (nextSort > prevSort + 1) {
            newSort = Math.floor((prevSort + nextSort) / 2);
          } else {
            // no gap: shift subsequent entries up by 1
            newSort = prevSort + 1;
            const toShiftRes: any = await client.from('group_itinerary_entries').select('id,sort_order').eq('group_id', groupId).eq('day_number', dayNumber).gte('sort_order', newSort).order('sort_order', { ascending: true });
            const toShift = toShiftRes && (toShiftRes.data ?? toShiftRes);
            if (Array.isArray(toShift) && toShift.length > 0) {
              // increment each item's sort_order by 1 (sequential updates)
              for (const row of toShift) {
                try {
                  await client.from('group_itinerary_entries').update({ sort_order: (row.sort_order ?? 0) + 1 }).eq('id', row.id);
                } catch (e) {
                  // non-fatal; continue
                }
              }
            }
          }
        } else {
          // no next: append after previous
          newSort = prevSort + 1;
        }
      }
    } else {
      // append to end of day
      const maxRes: any = await client.from('group_itinerary_entries').select('sort_order').eq('group_id', groupId).eq('day_number', dayNumber).order('sort_order', { ascending: false }).limit(1).maybeSingle();
      const maxRow = maxRes && (maxRes.data ?? maxRes);
      newSort = (maxRow && typeof maxRow.sort_order === 'number' ? maxRow.sort_order : 0) + 1;
    }

    const row: any = {
      group_id: groupId,
      day_number: dayNumber,
      day_date: dayDate || null,
      sort_order: newSort,
      item_type: 'note',
      creator_id: creatorId || null,
      card_id: null,
      note: noteText || null,
      start_time: start_time || null,
      end_time: end_time || null,
      custom_payload: {},
      inline_payload: null,
      parent_entry_id: parentEntryId || null,
      child_sort: typeof childSort === 'number' ? childSort : null,
    };

    const normalizedLink = typeof mapLink === 'string' ? mapLink.trim() : '';
    const normalizedMode = typeof mapLinkMode === 'string' ? mapLinkMode.trim() : '';
    const normalizedDuration = typeof mapLinkDuration === 'string' ? mapLinkDuration.trim() : '';
    if (normalizedLink || normalizedMode || normalizedDuration) {
      const payload: Record<string, string> = {};
      if (normalizedLink) payload.mapLink = normalizedLink;
      if (normalizedMode) payload.mapLinkMode = normalizedMode;
      if (normalizedDuration) payload.mapLinkDuration = normalizedDuration;
      row.custom_payload = payload;
      row.inline_payload = payload;
    }

    // Try insert; if DB schema doesn't yet include creator_id we will retry without it as a graceful fallback
    let insertRes: any = await client.from('group_itinerary_entries').insert([row]).select().maybeSingle();
    let inserted = insertRes && (insertRes.data ?? insertRes);
    let insertErr = insertRes && (insertRes.error ?? insertRes.error);

    if (insertErr) {
      const msg = String(insertErr?.message || insertErr?.msg || insertErr);
      // Supabase/PostgREST may report a schema cache error when an unknown column (creator_id) is present
      if (msg && /creator_id/.test(msg) && /schema|column|Could not find/i.test(msg)) {
        try {
          // remove creator_id and retry
          const rowNoCreator = { ...row };
          delete rowNoCreator.creator_id;
          const retryRes: any = await client.from('group_itinerary_entries').insert([rowNoCreator]).select().maybeSingle();
          const retryInserted = retryRes && (retryRes.data ?? retryRes);
          const retryErr = retryRes && (retryRes.error ?? retryRes.error);
          if (retryErr) {
            // still error; surface original
            throw retryErr;
          }
          // success on retry
          return retryInserted;
        } catch (e) {
          // rethrow original insert error for visibility
          throw insertErr;
        }
      }
      // other insert error -> bubble up
      throw insertErr;
    }
    return inserted;
  } catch (e) {
    throw e;
  }
}

export default { addNoteEntry };
